import styled, { CSSObject } from '@emotion/styled';
import { alpha } from '@mui/material';
import { VisitorCardStatus } from './VisitorCard';

export const Card = styled.article<{ $state: VisitorCardStatus; $compact: boolean; $clickable: boolean }>(
  ({ theme, $state, $compact, $clickable }) => {
    const tone = $state === 'entered' ? theme.palette.success.main : $state === 'error' ? theme.palette.error.main : null;
    const isEntered = $state === 'entered';
    return {
      background: isEntered ? alpha(theme.palette.success.main, 0.04) : 'transparent',
      border: `1px solid ${tone ? alpha(tone, isEntered ? 0.45 : 0.35) : alpha(theme.palette.text.primary, 0.14)}`,
      borderRadius: 12,
      padding: $compact ? '0.5rem 0.875rem' : '0.875rem 1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: $compact ? 0 : '0.375rem',
      cursor: $clickable ? 'pointer' : 'default',
      transition: 'border-color 220ms ease, background-color 220ms ease, padding 280ms cubic-bezier(0.2, 0.7, 0.2, 1), gap 280ms ease',
      '&:hover': {
        borderColor: tone ? alpha(tone, isEntered ? 0.6 : 0.5) : alpha(theme.palette.text.primary, 0.24),
      },
      [theme.breakpoints.up('md')]: {
        display: 'grid',
        gridTemplateColumns: $compact ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto',
        alignItems: 'center',
        columnGap: '1.5rem',
        transition:
          'border-color 220ms ease, background-color 220ms ease, padding 280ms cubic-bezier(0.2, 0.7, 0.2, 1), grid-template-columns 280ms ease, gap 280ms ease',
      },
    };
  },
);

export const EnteredButton = styled.div(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '6px 16px',
  borderRadius: 8,
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.success.main,
  background: alpha(theme.palette.success.main, 0.1),
  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
  whiteSpace: 'nowrap',
}));

export const Main = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    minWidth: 0,
  }),
);

export const HeaderRow = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    minWidth: 0,
  }),
);

export const Name = styled.span(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
}));

export const Meta = styled.span(({ theme }) => ({
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const EnteredMark = styled.span(({ theme }) => ({
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.success.main,
  whiteSpace: 'nowrap',
}));

export const PinRow = styled.div(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.6875rem',
  fontWeight: 500,
  lineHeight: 1.1,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: theme.palette.text.secondary,
  '& .pin-value': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", monospace',
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: 1.1,
    letterSpacing: '0.06em',
    color: theme.palette.text.secondary,
    textTransform: 'none',
  },
}));

export const Actions = styled.div(({ theme }) => ({
  display: 'flex',
  gap: 8,
  width: '100%',
  [theme.breakpoints.up('md')]: { width: 'auto', justifyContent: 'flex-end' },
  '& > *': { flex: 1, [theme.breakpoints.up('md')]: { flex: '0 0 auto' } },
}));
